import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button, ButtonProps } from '@/components/ui/button';
import { popModal } from '.';

const selectedViewModel = ({
  modalApi,
  initialShow,
}: {
  modalApi: NotificationModalApi;
  initialShow: boolean;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(initialShow);

  const closeModal = () => {
    popModal('NotificationModal');
    setShow(false);
    modalApi.onClose?.();
  };

  const cancelModal = () => {
    popModal('NotificationModal');
    setShow(false);
    modalApi.onCancel?.();
  };

  return {
    api: {
      start: () => {
        setLoading(true);
      },
      success: () => {
        setLoading(false);
        setShow(false);
      },
      failed: () => {
        setLoading(false);
      },
      close: closeModal,
      cancel: cancelModal,
    },
    data: {
      loading,
      show,
    },
  };
};
export type NotificationModalViewModel = ReturnType<typeof selectedViewModel>;

export type NotificationModalApi = {
  onClose?: () => void;
  onCancel?: () => void;
};

export type NotificationModalProps = {
  show?: boolean;
  title: string;
  description: string;
  btnLabel?: string;
  btnVariant?: ButtonProps['variant'];
} & NotificationModalApi;

export const NotificationModal = forwardRef(
  (
    {
      show = true,
      title,
      description,
      btnLabel = 'btn.close',
      btnVariant = 'default',
      onClose,
      onCancel,
    }: NotificationModalProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const viewModel = selectedViewModel({
      initialShow: show,
      modalApi: {
        onClose,
        onCancel,
      },
    });
    useImperativeHandle(ref, () => {
      return viewModel;
    }, []);

    return (
      <AlertDialog
        open={viewModel.data.show}
        onOpenChange={(open) => {
          if (!open) viewModel.api.close();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(title)}</AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{ __html: t(description) }}
            />
          </AlertDialogHeader>

          <AlertDialogFooter>
            <Button
              variant={btnVariant}
              onClick={viewModel.api.close}
              loading={viewModel.data.loading}
            >
              {t(btnLabel)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);
